import { IoBugSharp } from 'react-icons/io5';

import { ActionIcon, Button, Flex, Group, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { uid } from 'radash';
import { proxy } from 'valtio';

import type { NotificationId, NotificationProps } from './LocalNotificationService.types';

type ToastAction = {
    label: string;
    onClick: () => void;
};

type Toast = {
    id: string;
    title?: string;
    description: string;
    url?: string;
    action?: ToastAction;
    dismissed: boolean;
    timer?: ReturnType<typeof setTimeout>;
};

export const notificationsState = proxy({
    notifications: [] as Toast[],
    hovered: false,
});

const LocalNotificationService = {
    showToast: (args: { title?: string; description: string; url?: string; action?: ToastAction }) => {
        const { title, description, url, action } = args;

        const toast: Toast = {
            id: uid(4),
            title,
            description,
            url,
            action,
            dismissed: false,
        };

        notificationsState.notifications.push(toast);

        if (!notificationsState.hovered) {
            LocalNotificationService.scheduleDismissToast(toast);
        }
    },

    scheduleDismissToast: (toast: Toast) => {
        toast.timer = setTimeout(() => {
            LocalNotificationService.dismissToast(toast.id);
        }, 5000);
    },

    dismissToast: (id: string) => {
        notificationsState.notifications.forEach((notification) => {
            if (notification.id === id) {
                clearTimeout(notification.timer);
                notification.timer = undefined;
                notification.dismissed = true;
            }
        });
    },

    stopToastScheduler: () => {
        notificationsState.hovered = true;
        notificationsState.notifications.forEach((notification) => {
            if (notification.timer) {
                clearTimeout(notification.timer);
                notification.timer = undefined;
            }
        });
    },

    startToastScheduler: () => {
        notificationsState.hovered = false;
        notificationsState.notifications.forEach((notification) => {
            if (!notification.dismissed) {
                LocalNotificationService.scheduleDismissToast(notification);
            }
        });
    },

    show: (data: NotificationProps): NotificationId => {
        const { actions = [], message, ..._data } = data;

        const notificationId = notifications.show({
            ..._data,
            message:
                actions.length > 0 ? (
                    <Flex justify="space-between" gap={'xs'} align="center">
                        <span>{message}</span>
                        <Group>
                            {actions.map(({ icon, onClick, tooltip, label }, index) => {
                                if (tooltip && icon) {
                                    return (
                                        <Tooltip key={index} label={tooltip}>
                                            <ActionIcon
                                                key={index}
                                                radius="md"
                                                variant="outline"
                                                color="white"
                                                onClick={() => {
                                                    onClick();
                                                    LocalNotificationService.hide(notificationId);
                                                }}
                                            >
                                                {icon}
                                            </ActionIcon>
                                        </Tooltip>
                                    );
                                }

                                if (label) {
                                    return (
                                        <Button
                                            key={index}
                                            size="compact-xs"
                                            variant="outline"
                                            color="white"
                                            onClick={() => {
                                                onClick();
                                                LocalNotificationService.hide(notificationId);
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    );
                                }

                                return null;
                            })}
                        </Group>
                    </Flex>
                ) : (
                    message
                ),
        });

        return notificationId;
    },

    showInfo: (data: NotificationProps): NotificationId => {
        return LocalNotificationService.show({
            ...data,
            color: 'transparent',
        });
    },

    showSuccess: (data: NotificationProps): NotificationId => {
        return LocalNotificationService.show({
            ...data,
            color: 'transparent',
        });
    },

    showError: (data: NotificationProps): NotificationId => {
        return LocalNotificationService.show({
            ...data,
            color: 'transparent',
        });
    },

    showPersistantBug: (data: NotificationProps): NotificationId => {
        return LocalNotificationService.show({
            autoClose: false,
            color: 'red',
            style: {
                borderRadius: 'var(--mantine-radius-sm)',
            },
            icon: <IoBugSharp />,
            ...data,
        });
    },

    hide: (id: NotificationId | undefined) => {
        if (!id) return;

        notifications.hide(id);
    },

    clean: notifications.clean,
};

export { LocalNotificationService };
